import {
	getGridData,
	getGridDataResolved,
	getGridDataRejected,
} from '../assetGridSlice';

import {
	Asset,
	PaginatedAssets,
	GetGridDataAction,
	PaginatedResponse,
} from '../../utils/types';
import {
	put,
	retry,
	select,
	call,
	takeLatest,
} from 'redux-saga/effects';

import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { AssetSchema } from '@innovyze/lib_asset_schema';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
	getAssetSchemaSaga,
} from '@innovyze/lib_asset_schema_store';
import { getAssetsPaginated } from '../../../../services/asset';
import { getSpatialDataPaginated } from '../../../../services/risk';
import { SpatialRecord } from '../../types/risk.types';
import { resolveServerField } from '../../utils/grid';

function* getAssetSchemaForGrid(assetType: string) {
	let assetSchema: AssetSchema | undefined = yield select(
		assetSchemaSelectorByType,
		assetType,
	);

	if (!assetSchema) {
		// Schema fetch failures propagate to getGridDataSaga's catch block (same
		// pattern as getAssetWithExtendedDataSaga in redux/asset/sagas.ts).
		yield call(getAssetSchemaSaga, getAssetSchemaAction({ assetType }));
		assetSchema = yield select(assetSchemaSelectorByType, assetType);
	}

	return assetSchema;
}

function* getGridDataSaga(action: PayloadAction<GetGridDataAction>) {
	const {
		assetType,
		systemType,
		limit,
		offset,
		sort = 'assetId',
		sortDescending,
		filterModel,
		exclude,
	} = action.payload;
	try {
		const assetSchema =
			systemType === 'SpatialData'
				? undefined
				: yield* getAssetSchemaForGrid(assetType);
		const serverSortField = resolveServerField(sort, assetSchema) ?? sort;

		const payload: AxiosResponse<PaginatedResponse<Asset | SpatialRecord>> =
			systemType === 'SpatialData'
				? yield* getSpatialData(
						assetType,
						limit,
						offset,
						serverSortField,
						sortDescending,
						filterModel,
						exclude,
				  )
				: yield retry(
						5,
						1500,
						getAssetsPaginated,
						systemType,
						assetType,
						limit,
						offset,
						serverSortField,
						sortDescending,
						filterModel,
						exclude,
				  );

		if (payload.data) {
			yield put(
				getGridDataResolved({
					...payload.data,
					pagination: { ...payload.data.pagination, limit },
					sort,
					sortDescending,
					filterModel,
				}),
			);
		} else {
			yield put(getGridDataRejected());
		}
	} catch (e) {
		yield put(getGridDataRejected());
	}
}

function* getSpatialData(
	layerName: string,
	limit: number,
	offset: number | undefined,
	sort: string,
	sortDescending: boolean | undefined,
	filterModel: PaginatedAssets['filterModel'],
	exclude: PaginatedAssets['exclude'],
) {
	if (!layerName) {
		throw new Error('Layer ID not found');
	}

	const result: AxiosResponse<PaginatedResponse<SpatialRecord>> = yield retry(
		3,
		1500,
		getSpatialDataPaginated,
		layerName,
		limit,
		offset,
		sort,
		sortDescending,
		filterModel,
		exclude,
	);

	if (!result.data) {
		return result;
	}

	return {
		data: {
			...result.data,
			items: result.data.items.map(({ id, ...item }) => ({
				_id: id,
				...item,
			})),
		},
	};
}

function* watchGetGridData() {
	yield takeLatest(getGridData, getGridDataSaga);
}

export default [watchGetGridData()];
